import React from 'react';
import { Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  isLoading = false
}) => {
  const { t } = useI18n();

  const defaultTitle = title || t('modals.delete_confirmation');
  const defaultMessage = message || t('modals.are_you_sure_delete');
  const defaultConfirmText = confirmText || t('common.delete');
  const defaultCancelText = cancelText || t('common.cancel');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-md bg-white rounded-2xl p-6">
        <DialogTitle className="sr-only">{defaultTitle}</DialogTitle>
        <SossietyDialogHeader title={defaultTitle}>
          <SossietyDialogClose onClick={onClose} />
        </SossietyDialogHeader>

        <div className="space-y-6">
          {/* Icon and Message */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              {defaultMessage}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 text-black border-black hover:bg-gray-100 rounded-full"
              disabled={isLoading}
            >
              {defaultCancelText}
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full"
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : defaultConfirmText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 